import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { HistoryOutlined, LocalPrintshopOutlined, SearchOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import CustomModal from "../../Components/CustomModal";
import CustomSnackBar from "../../Components/CustomSnackBar";
import DatePick from "../../Components/DatePick";
import MainText from "../../Components/MainText";
import SaleTicket, { printSaleTicket } from "../../Components/sale/SaleTicket";
import { hasPermission } from "../../config/permissionUtils";
import { useAuth } from "../../context/AuthContext";
import { saleService } from "../../services/modules/sale.service";
import { buildSalesDraftPath, createSalesDraftId } from "../../state/salesDraftUtils";

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const toMoney = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "Q0.00";
  return `Q${parsed.toFixed(2)}`;
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const toQueryDate = (value) => {
  if (!value) return undefined;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString().slice(0, 10);
};

const methodLabel = (methodCodeOrName) => {
  const raw = String(methodCodeOrName || "").trim();
  const normalized = raw.toUpperCase();
  if (!normalized) return "-";
  if (normalized === "CASH" || normalized === "EFECTIVO") return "Efectivo";
  if (normalized === "CARD" || normalized === "TARJETA") return "Tarjeta";
  if (normalized === "TRANSFER" || normalized === "TRANSFERENCIA") return "Transferencia";
  return raw;
};

const getPrimaryPaymentLabel = (sale) => {
  if (sale?.primary_payment_label) return methodLabel(sale.primary_payment_label);
  const payments = toArray(sale?.payments);
  const firstPayment = payments[0];
  if (firstPayment?.payment_method_name) return methodLabel(firstPayment.payment_method_name);
  if (firstPayment?.payment_method_code) return methodLabel(firstPayment.payment_method_code);
  if (sale?.payment_method_name) return methodLabel(sale.payment_method_name);
  if (sale?.payment_method_code) return methodLabel(sale.payment_method_code);
  return "-";
};

const getStatusMeta = (status, sale = null) => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "COMPLETED" && toNumber(sale?.returned_total) > 0) {
    return { label: "Devolucion parcial", color: "warning" };
  }
  if (normalized === "PENDING") return { label: "Pendiente", color: "warning" };
  if (normalized === "WAITING_PAYMENT") return { label: "Esperando transferencia", color: "info" };
  if (normalized === "IN_CASHIER") return { label: "En caja", color: "info" };
  if (normalized === "COMPLETED") return { label: "Completada", color: "success" };
  if (normalized === "RETURNED") return { label: "Devuelta", color: "warning" };
  if (normalized === "VOIDED") return { label: "Anulada", color: "error" };
  if (normalized === "CANCELED") return { label: "Cancelada", color: "default" };
  if (normalized === "DRAFT") return { label: "Borrador", color: "default" };
  return { label: normalized || "-", color: "default" };
};

const getSoldBy = (sale) =>
  sale?.sold_by_display ||
  sale?.sold_by_name ||
  sale?.sold_by?.name ||
  sale?.created_by_name ||
  sale?.created_by?.name ||
  "-";

const getAuthorizedBy = (sale) =>
  sale?.authorized_by_display ||
  sale?.authorized_by_name ||
  sale?.authorized_by?.name ||
  (String(sale?.status || "").toUpperCase() === "WAITING_PAYMENT"
    ? "Pendiente autorizacion"
    : "-");

const getRemainingReturnQty = (item) =>
  Math.max(0, toNumber(item?.remaining_qty_presentation ?? item?.qty_presentation) || 0);

const calculateReturnTotal = (sale, quantities = {}) =>
  toArray(sale?.items).reduce((total, item) => {
    const qty = Number(quantities?.[item.id] || 0);
    if (!Number.isInteger(qty) || qty <= 0) return total;
    const soldQty = Math.max(1, toNumber(item?.qty_presentation));
    const remainingQty = getRemainingReturnQty(item);
    const remainingAmount = Math.max(0, toNumber(item?.line_total) - toNumber(item?.returned_line_total));
    const amount = qty === remainingQty ? remainingAmount : (toNumber(item?.line_total) / soldQty) * qty;
    return total + Math.min(amount, remainingAmount);
  }, 0);

function SaleDetailContent({
  sale,
  loading,
  onPrint,
  ticketBranchName,
  ticketCashierName,
  canReturnSale = false,
  onRequestReturn,
  returning = false,
}) {
  if (loading) {
    return (
      <Typography variant="body2" color="text.secondary">
        Cargando detalle...
      </Typography>
    );
  }

  if (!sale) {
    return (
      <Typography variant="body2" color="text.secondary">
        No hay detalle disponible para esta venta.
      </Typography>
    );
  }

  const statusMeta = getStatusMeta(sale.status, sale);
  const firstPayment = toArray(sale.payments)[0] || null;
  const hasReturnableItems = toArray(sale.items).some((item) => getRemainingReturnQty(item) > 0);
  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ md: "center" }}
        spacing={1}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {sale.sale_number || "Ticket"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatDateTime(sale.created_at)}
          </Typography>
        </Box>
        <Chip size="small" label={statusMeta.label} color={statusMeta.color} />
      </Stack>

      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        <Chip size="small" variant="outlined" label={`Cliente: ${sale.customer_display || "CF"}`} />
        <Chip size="small" variant="outlined" label={`Sucursal: ${sale.branch_name || "-"}`} />
        <Chip size="small" variant="outlined" label={`Cajero: ${getSoldBy(sale)}`} />
      </Stack>

      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack spacing={1.1}>
          {toArray(sale.items).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Sin items.
            </Typography>
          ) : (
            toArray(sale.items).map((item) => (
              <Stack
                key={item.id}
                direction="row"
                justifyContent="space-between"
                spacing={1.2}
                alignItems="flex-start"
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                    {item.product_description || "-"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.presentation_name || "-"} - {item.qty_presentation || 0} x {toMoney(item.unit_price)}
                  </Typography>
                  {toNumber(item?.returned_qty_presentation) > 0 ? (
                    <Typography variant="caption" color="warning.main" display="block">
                      Devuelto: {item.returned_qty_presentation} / Restante: {item.remaining_qty_presentation}
                    </Typography>
                  ) : null}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                  {toMoney(item.line_total)}
                </Typography>
              </Stack>
            ))
          )}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack spacing={0.7}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              Subtotal
            </Typography>
            <Typography variant="body2">{toMoney(sale.subtotal)}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              Descuento
            </Typography>
            <Typography variant="body2">{toMoney(sale.discount_total)}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              Impuestos
            </Typography>
            <Typography variant="body2">{toMoney(sale.tax_total)}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              Total
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              {toMoney(sale.total)}
            </Typography>
          </Stack>
          {toNumber(sale?.returned_total) > 0 ? (
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="warning.main" sx={{ fontWeight: 700 }}>
                Devuelto acumulado
              </Typography>
              <Typography variant="body2" color="warning.main" sx={{ fontWeight: 700 }}>
                {toMoney(sale.returned_total)}
              </Typography>
            </Stack>
          ) : null}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack spacing={0.7}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Pago
          </Typography>
          <Typography variant="body2">Método: {getPrimaryPaymentLabel(sale)}</Typography>
          <Typography variant="body2">Autorizado por: {getAuthorizedBy(sale)}</Typography>
          <Typography variant="body2">Recibido: {toMoney(firstPayment?.cash_received)}</Typography>
          <Typography variant="body2">Cambio: {toMoney(firstPayment?.change_given)}</Typography>
          <Typography variant="caption" color="text.secondary">
            Referencia: {firstPayment?.reference || "-"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Completada: {formatDateTime(sale.completed_at)}
          </Typography>
          {sale?.returned_at ? (
            <Typography variant="caption" color="text.secondary">
              Devuelta: {formatDateTime(sale.returned_at)}
            </Typography>
          ) : null}
        </Stack>
      </Paper>

      {sale?.return_reason ? (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Stack spacing={0.5}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Devolucion registrada
            </Typography>
            <Typography variant="body2">{sale.return_reason}</Typography>
            <Typography variant="caption" color="text.secondary">
              Registrada por: {sale.returned_by_name || "-"}
            </Typography>
          </Stack>
        </Paper>
      ) : null}

      <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
        <SaleTicket
          sale={sale}
          branchName={ticketBranchName}
          cashierName={ticketCashierName}
        />
      </Box>

      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        {canReturnSale &&
        String(sale?.status || "").toUpperCase() === "COMPLETED" &&
        hasReturnableItems ? (
          <Button
            color="warning"
            variant="contained"
            onClick={() => onRequestReturn?.(sale)}
            disabled={returning}
          >
            {returning ? "Procesando..." : "Registrar devolucion"}
          </Button>
        ) : null}
        <Button variant="outlined" startIcon={<LocalPrintshopOutlined />} onClick={() => onPrint(sale)}>
          Imprimir ticket
        </Button>
      </Stack>
    </Stack>
  );
}

function SalesHistoryPage() {
  const navigate = useNavigate();
  const { currentBranch, permissions = [], isSuperuser } = useAuth();
  const branchId = currentBranch?.id || null;
  const today = useMemo(() => new Date(), []);
  const canReturnSale = hasPermission(permissions, "sales_return_create", isSuperuser);
  const canApproveSpecialReturn = hasPermission(permissions, "sales_return_approve", isSuperuser);

  const [filters, setFilters] = useState({
    date_from: today,
    date_to: today,
    q: "",
    status: "COMPLETED,RETURNED,VOIDED",
  });
  const [appliedFilters, setAppliedFilters] = useState({
    date_from: today,
    date_to: today,
    q: "",
    status: "COMPLETED,RETURNED,VOIDED",
  });
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailSale, setDetailSale] = useState(null);
  const [returnDialog, setReturnDialog] = useState({
    open: false,
    sale: null,
    reason: "",
    quantities: {},
  });
  const [returnSubmitting, setReturnSubmitting] = useState(false);
  const returnContext = returnDialog.sale?.return_context || {};
  const returnBlockedByCash = !returnContext.current_cash_session_id;
  const returnBlockedByApproval =
    Boolean(returnContext.requires_special_approval) && !canApproveSpecialReturn;
  const returnTotal = useMemo(
    () => calculateReturnTotal(returnDialog.sale, returnDialog.quantities),
    [returnDialog.quantities, returnDialog.sale]
  );

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  const showSnack = (message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  };

  const loadRows = useCallback(async () => {
    if (!branchId || !toQueryDate(appliedFilters.date_from)) {
      setRows([]);
      setRowCount(0);
      return;
    }

    setLoading(true);
    try {
      const data = await saleService.listSales({
        branch_id: branchId,
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
        date_from: toQueryDate(appliedFilters.date_from),
        date_to: toQueryDate(appliedFilters.date_to),
        q: appliedFilters.q || undefined,
        status:
          appliedFilters.status && !String(appliedFilters.status).includes(",")
            ? appliedFilters.status
            : undefined,
        statuses:
          appliedFilters.status && String(appliedFilters.status).includes(",")
            ? appliedFilters.status
            : undefined,
      });

      const list = toArray(data);
      setRows(list);
      setRowCount(Number(data?.count || 0));
    } catch {
      setRows([]);
      setRowCount(0);
      showSnack("No se pudo cargar el historial de ventas.", "error");
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, branchId, paginationModel.page, paginationModel.pageSize]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const handleApplyFilters = useCallback(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setAppliedFilters(filters);
  }, [filters]);

  const handleOpenDetail = useCallback(
    async (row) => {
      if (!row?.id || !branchId) return;
      setDetailOpen(true);
      setDetailLoading(true);
      try {
        const detail = await saleService.getSale(row.id, { branch_id: branchId });
        setDetailSale(detail || null);
      } catch {
        setDetailSale(null);
        showSnack("No se pudo cargar el detalle de la venta.", "error");
      } finally {
        setDetailLoading(false);
      }
    },
    [branchId]
  );

  const handlePrintTicket = useCallback(
    (sale) => {
      if (!sale) return;
      printSaleTicket({
        sale,
        branchName: sale?.branch_name || currentBranch?.name || "-",
        cashierName: getSoldBy(sale),
      });
    },
    [currentBranch?.name]
  );

  const handleReprintTicket = useCallback(
    async (row) => {
      if (!row?.id || !branchId) return;
      try {
        const detail = row?.items ? row : await saleService.getSale(row.id, { branch_id: branchId });
        handlePrintTicket(detail);
      } catch {
        showSnack("No se pudo preparar el ticket para impresion.", "error");
      }
    },
    [branchId, handlePrintTicket]
  );

  const handleConfirmReturn = useCallback(async () => {
    const sale = returnDialog.sale;
    const reason = String(returnDialog.reason || "").trim();
    if (!sale?.id || !branchId) return;
    if (returnBlockedByCash) {
      showSnack("Debes abrir caja para registrar la devolución en efectivo.", "warning");
      return;
    }
    if (returnBlockedByApproval) {
      showSnack("La venta pertenece a una caja ya conciliada. Requiere autorización para devolución.", "warning");
      return;
    }
    if (!reason) {
      showSnack("Debes ingresar el motivo de la devolucion.", "warning");
      return;
    }
    const items = toArray(sale?.items)
      .map((item) => ({
        item_id: item.id,
        qty_presentation: Number(returnDialog.quantities?.[item.id] || 0),
      }))
      .filter((item) => Number.isInteger(item.qty_presentation) && item.qty_presentation > 0);
    if (items.length === 0) {
      showSnack("Debes indicar al menos un item para devolver.", "warning");
      return;
    }

    setReturnSubmitting(true);
    try {
      const updatedSale = await saleService.returnSale(sale.id, {
        branch_id: branchId,
        reason,
        items,
      });
      setDetailSale(updatedSale || null);
      setReturnDialog({ open: false, sale: null, reason: "", quantities: {} });
      showSnack("Devolucion registrada correctamente.", "success");
      await loadRows();
    } catch (error) {
      const detail = error?.response?.data?.detail || "No se pudo registrar la devolucion.";
      showSnack(detail, "error");
    } finally {
      setReturnSubmitting(false);
    }
  }, [
    branchId,
    loadRows,
    returnBlockedByApproval,
    returnBlockedByCash,
    returnDialog.quantities,
    returnDialog.reason,
    returnDialog.sale,
  ]);

  const columns = useMemo(
    () => [
      {
        field: "sale_number",
        headerName: "Ticket",
        minWidth: 160,
        flex: 0.8,
      },
      {
        field: "customer_display",
        headerName: "Cliente",
        minWidth: 180,
        flex: 1,
        valueGetter: (_, row) => row?.customer_display || "CF",
      },
      {
        field: "created_at",
        headerName: "Fecha / Hora",
        minWidth: 210,
        flex: 1,
        valueGetter: (_, row) => formatDateTime(row?.created_at),
      },
      {
        field: "status",
        headerName: "Estado",
        minWidth: 180,
        flex: 0.8,
        renderCell: ({ row }) => {
          const meta = getStatusMeta(row?.status, row);
          return <Chip size="small" label={meta.label} color={meta.color} variant="outlined" />;
        },
      },
      {
        field: "primary_payment_label",
        headerName: "Método pago",
        minWidth: 170,
        flex: 0.8,
        valueGetter: (_, row) => getPrimaryPaymentLabel(row),
      },
      {
        field: "sold_by_display",
        headerName: "Vendedor",
        minWidth: 180,
        flex: 0.9,
        valueGetter: (_, row) => getSoldBy(row),
      },
      {
        field: "total",
        headerName: "Total",
        minWidth: 130,
        flex: 0.6,
        valueGetter: (_, row) => toMoney(row?.total),
      },
      {
        field: "actions",
        headerName: "Acciones",
        minWidth: 160,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Button
            size="small"
            variant="outlined"
            startIcon={<LocalPrintshopOutlined fontSize="small" />}
            onClick={(event) => {
              event.stopPropagation();
              handleReprintTicket(row);
            }}
          >
            Reimprimir
          </Button>
        ),
      },
    ],
    [handleReprintTicket]
  );

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <MainText
          title="Historial de ventas"
          icon={HistoryOutlined}
          subtitle="Consulta operativa de tickets y cobros"
        />
        <Button
          variant="outlined"
          onClick={() => navigate(buildSalesDraftPath(createSalesDraftId()))}
        >
          Nueva venta
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ p: 1.2 }}>
        <Stack direction={{ xs: "column", lg: "row" }} spacing={1} alignItems={{ lg: "center" }}>
          <TextField
            size="small"
            label="Buscar"
            value={filters.q}
            onChange={(event) => setFilters((prev) => ({ ...prev, q: event.target.value }))}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 240 }}
          />
          <DatePick
            name="date_from"
            label="Desde"
            value={filters.date_from}
            onChange={(name, value) => setFilters((prev) => ({ ...prev, [name]: value }))}
            size="small"
            allowHistoricalDates
          />
          <DatePick
            name="date_to"
            label="Hasta"
            value={filters.date_to}
            onChange={(name, value) => setFilters((prev) => ({ ...prev, [name]: value }))}
            size="small"
            allowHistoricalDates
          />
          <TextField
            select
            size="small"
            label="Estado"
            value={filters.status}
            onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="COMPLETED,RETURNED,VOIDED">Finalizadas</MenuItem>
            <MenuItem value="COMPLETED">Completadas</MenuItem>
            <MenuItem value="RETURNED">Devueltas</MenuItem>
            <MenuItem value="VOIDED">Anuladas</MenuItem>
          </TextField>
          <Button variant="contained" size="small" onClick={handleApplyFilters}>
            Buscar
          </Button>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ height: "calc(100vh - 320px)", minHeight: 420 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          rowCount={rowCount}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          onRowClick={(params) => handleOpenDetail(params.row)}
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#757B2B",
              color: "#ffffff",
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:hover":
            {
              backgroundColor: "#757B2B !important",
            },
            "& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-menuIcon, & .MuiDataGrid-columnSeparator":
            {
              color: "#ffffff !important",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "rgba(16, 23, 60, 0.08)",
            },
          }}
        />
      </Paper>

      <CustomModal
        open={detailOpen}
        setOpen={setDetailOpen}
        width="760px"
        component={SaleDetailContent}
        componentProps={{
          sale: detailSale,
          loading: detailLoading,
          onPrint: handlePrintTicket,
          ticketBranchName: detailSale?.branch_name || currentBranch?.name || "-",
          ticketCashierName: getSoldBy(detailSale),
          canReturnSale,
          onRequestReturn: (sale) =>
            setReturnDialog({
              open: true,
              sale,
              reason: "",
              quantities: {},
            }),
          returning: returnSubmitting,
        }}
      />

      <Dialog
        open={returnDialog.open}
        onClose={() => {
          if (returnSubmitting) return;
          setReturnDialog({ open: false, sale: null, reason: "", quantities: {} });
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Registrar devolucion</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25}>
            <Typography variant="body2" color="text.secondary">
              Se restaurara inventario y se registrara la salida correspondiente en la caja abierta actual.
            </Typography>
            <Alert severity="info">
              Para cambios de producto, registre primero la devolución y luego realice una nueva venta.
            </Alert>
            {returnBlockedByCash ? (
              <Alert severity="error">
                Debes abrir caja para registrar la devolución en efectivo.
              </Alert>
            ) : (
              <Alert severity="success">
                Se registrara en Caja actual #{returnContext.current_cash_session_id}
                {returnContext.current_cash_register_name ? ` - ${returnContext.current_cash_register_name}` : ""}.
              </Alert>
            )}
            {returnContext.original_cash_session_closed && !returnContext.original_cash_session_reconciled ? (
              <Alert severity="warning">
                La venta pertenece a una caja cerrada. La devolución se registrará en la caja actual.
              </Alert>
            ) : null}
            {returnContext.original_cash_session_reconciled ? (
              <Alert severity={returnBlockedByApproval ? "error" : "warning"}>
                La venta pertenece a una caja ya conciliada. Requiere autorización para devolución.
              </Alert>
            ) : null}
            <Typography variant="body2">
              Ticket: {returnDialog.sale?.sale_number || "-"}
            </Typography>
            <Stack spacing={1}>
              {toArray(returnDialog.sale?.items)
                .filter((item) => getRemainingReturnQty(item) > 0)
                .map((item) => (
                  <Paper key={item.id} variant="outlined" sx={{ p: 1 }}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      justifyContent="space-between"
                      alignItems={{ sm: "center" }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                          {item.product_description || "-"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Vendido: {item.qty_presentation} / Ya devuelto: {item.returned_qty_presentation || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Disponible para devolver: {item.remaining_qty_presentation} {item.presentation_name || ""}
                        </Typography>
                      </Box>
                      <TextField
                        size="small"
                        type="number"
                        label="Devolver"
                        value={returnDialog.quantities?.[item.id] || ""}
                        onChange={(event) =>
                          setReturnDialog((prev) => ({
                            ...prev,
                            quantities: {
                              ...prev.quantities,
                              [item.id]: event.target.value,
                            },
                          }))
                        }
                        inputProps={{
                          min: 0,
                          step: 1,
                          max: getRemainingReturnQty(item),
                        }}
                        sx={{ width: 130 }}
                      />
                    </Stack>
                  </Paper>
                ))}
            </Stack>
            <TextField
              label="Motivo de la devolucion"
              value={returnDialog.reason}
              onChange={(event) =>
                setReturnDialog((prev) => ({
                  ...prev,
                  reason: event.target.value,
                }))
              }
              fullWidth
              size="small"
              multiline
              minRows={3}
              required
            />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="subtitle2">Total a devolver</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                {toMoney(returnTotal)}
              </Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setReturnDialog({ open: false, sale: null, reason: "", quantities: {} })}
            disabled={returnSubmitting}
          >
            Cancelar
          </Button>
          <Button
            color="warning"
            variant="contained"
            onClick={handleConfirmReturn}
            disabled={
              returnSubmitting ||
              returnBlockedByCash ||
              returnBlockedByApproval ||
              returnTotal <= 0 ||
              !String(returnDialog.reason || "").trim()
            }
          >
            {returnSubmitting ? "Procesando..." : "Confirmar devolucion"}
          </Button>
        </DialogActions>
      </Dialog>

      <CustomSnackBar open={snackOpen} setOpen={setSnackOpen} message={snackMsg} severity={snackSev} />
    </Box>
  );
}

export default SalesHistoryPage;
